/**
 * Toast store. A module-level singleton rather than context so any module
 * (mutation handlers, the live feed, the service worker bridge) can raise one
 * without threading a hook through. `<Toaster />` subscribes to it.
 */
import { useSyncExternalStore } from 'react';

export type ToastTone = 'info' | 'success' | 'error';

export interface ToastAction {
  label: string;
  /** Returning a promise keeps the toast alive until it settles. */
  run: () => void | Promise<void>;
}

export interface Toast {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
  /** Undo affordance for reversible actions (ack, snooze, assign). */
  action?: ToastAction;
  durationMs: number;
}

export interface ToastInput {
  tone?: ToastTone;
  title: string;
  description?: string;
  action?: ToastAction;
  durationMs?: number;
}

let nextId = 1;
let toasts: Toast[] = [];
const listeners = new Set<() => void>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function pushToast(input: ToastInput): number {
  const toast: Toast = {
    id: nextId++,
    tone: input.tone ?? 'info',
    title: input.title,
    ...(input.description === undefined ? {} : { description: input.description }),
    ...(input.action === undefined ? {} : { action: input.action }),
    durationMs: input.durationMs ?? (input.action ? 8000 : 4500),
  };
  toasts = [...toasts, toast];
  emit();
  if (toast.durationMs > 0) {
    timers.set(
      toast.id,
      setTimeout(() => dismissToast(toast.id), toast.durationMs),
    );
  }
  return toast.id;
}

export function dismissToast(id: number): void {
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
  const next = toasts.filter((t) => t.id !== id);
  if (next.length === toasts.length) return;
  toasts = next;
  emit();
}

export function clearToasts(): void {
  for (const timer of timers.values()) clearTimeout(timer);
  timers.clear();
  toasts = [];
  emit();
}

export const toast = {
  info: (title: string, rest: Omit<ToastInput, 'title' | 'tone'> = {}) =>
    pushToast({ ...rest, title, tone: 'info' }),
  success: (title: string, rest: Omit<ToastInput, 'title' | 'tone'> = {}) =>
    pushToast({ ...rest, title, tone: 'success' }),
  error: (title: string, rest: Omit<ToastInput, 'title' | 'tone'> = {}) =>
    pushToast({ ...rest, title, tone: 'error' }),
};

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): Toast[] {
  return toasts;
}

const EMPTY: Toast[] = [];

export function useToasts(): Toast[] {
  return useSyncExternalStore(subscribe, snapshot, () => EMPTY);
}
